import { WorkingDaysModel } from "client/Api/UserProfile/service";
import {
	TagColor,
	TagList,
	TagModel
} from "client/Components";
import {
	DayOfWeek,
	DayOfWeekFormat,
	formatDayOfWeek,
	getFirstWeekDay,
	getWeekDays
} from "client/DateAndTime/culture";
import { generateTrackingId } from "client/Misc/utils";
import { ReactElement, useMemo } from "react";

export type WorkingDaysProps = {
	data: WorkingDaysModel;
	format: DayOfWeekFormat;
	trackingId: string;
	color: TagColor;
};

export const WorkingDays = (props: WorkingDaysProps): ReactElement => {
	const {
		data,
		format,
		color,
		trackingId
	} = props;

	const firstWeekDay: DayOfWeek = getFirstWeekDay();

	const weekDays = useMemo(
		(): readonly DayOfWeek[] => getWeekDays(firstWeekDay),
		[firstWeekDay]
	);

	const isWorkingDay = useMemo(
		(): { [key in DayOfWeek]: boolean; } => {
			return {
				[DayOfWeek.Monday]: data.monday,
				[DayOfWeek.Tuesday]: data.tuesday,
				[DayOfWeek.Wednesday]: data.wednesday,
				[DayOfWeek.Thursday]: data.thursday,
				[DayOfWeek.Friday]: data.friday,
				[DayOfWeek.Saturday]: data.saturday,
				[DayOfWeek.Sunday]: data.sunday
			};
		},
		[data]
	);

	const weekTagModels = useMemo(
		(): readonly TagModel<DayOfWeek>[] => {
			return weekDays.map(
				day => ({
					text: formatDayOfWeek(day, format),
					value: day,
					color,
					isDisabled: !isWorkingDay[day],
					valueTrackingId: generateTrackingId(
						isWorkingDay[day]
							? "working-day"
							: "non-working-day",
						`|${day}`
					)
				})
			);
		},
		[
			isWorkingDay,
			weekDays,
			format,
			color
		]
	);

	return (
		<TagList
			tags={weekTagModels}
			responsiveBehavior="wrap"
			areTagsAlreadyPreordered
			trackingId={trackingId}
		/>
	);
};
WorkingDays.displayName = "WorkingDays";